import React, { useState, useRef } from 'react';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import { imageTypes } from "@pb/utils/image/getImageUrl";
import { default as waitUntil } from "@pb/utils/common/waitUntil";
import { default as consoleEvents } from "@pb/utils/consoleEvents";
import { default as isTouchDeviceUtil } from "@pb/utils/isTouchDevice";
import { default as IS_CLIENT } from "@pb/utils/constants/isClient";
import IntersectionVisible from "../../helpers/IntersectionVisible";
import Image from "../../ui/Image";
import PlayVideoToggleButton from "../../ui/PlayVideoToggleButton";
import { getVideoLabels } from './helpers/getVideoLabels';
import { getVideoOptions } from './helpers/getVideoOptions';
import getAkamaiVideoUrl from './helpers/getVideoUrlAkamai';
import { setVideoSchema } from './helpers/setVideoSchema';
import { trackVideo } from './helpers/tracking';
import styles from './ProductVideo.module.scss';
const ProductVideo = ({ articleCode, videoId, description, name, posterImage, intersectionVisibleOptions, reserveHeightAspectRatio = '2/3', carouselIsSliding = false, clickable = true, isPlpVideo = false, shouldUseAkamai = false, }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoLoadingFailed, setVideoLoadingFailed] = useState(false);
    const [isStarted, setIsStarted] = useState(false);
    const [player, setPlayer] = useState({ pause: () => { }, play: () => { }, options_: {} });
    const labels = getVideoLabels();
    const options = getVideoOptions(isPlpVideo);
    const isTouchDevice = isTouchDeviceUtil();
    const videoPlayerRef = useRef();
    const akamaiVideoRef = useRef(null);
    const playVideo = () => {
        if (player) {
            player.play();
        }
        setIsPlaying(true);
    };
    const pauseVideo = () => {
        if (player) {
            player.pause();
        }
        setIsPlaying(false);
    };
    const handleOnClick = () => {
        if (carouselIsSliding || !clickable) {
            return;
        }
        if (!isPlaying) {
            playVideo();
        }
        else {
            pauseVideo();
        }
        if (!isStarted) {
            setIsStarted(true);
        }
    };
    const handleOnHide = () => {
        var _a;
        if (isPlaying) {
            pauseVideo();
            setIsPlaying(false);
        }
        if (shouldUseAkamai) {
            (_a = akamaiVideoRef === null || akamaiVideoRef === void 0 ? void 0 : akamaiVideoRef.current) === null || _a === void 0 ? void 0 : _a.pause();
        }
    };
    const handleOnShow = () => {
        var _a;
        // Play Videos on PLP when in view
        if (isPlpVideo && !videoLoadingFailed) {
            playVideo();
            setIsStarted(true);
        }
        if (shouldUseAkamai) {
            (_a = akamaiVideoRef === null || akamaiVideoRef === void 0 ? void 0 : akamaiVideoRef.current) === null || _a === void 0 ? void 0 : _a.play();
        }
        return null;
    };
    const onSuccess = (success) => {
        var _a, _b;
        const { ref } = success;
        const videoPlayerRefNode = (_a = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.refNode;
        const videoIdIsSet = () => {
            var _a, _b;
            return ((_a = ref === null || ref === void 0 ? void 0 : ref.mediainfo) === null || _a === void 0 ? void 0 : _a.id) !== undefined && ((_b = ref === null || ref === void 0 ? void 0 : ref.mediainfo) === null || _b === void 0 ? void 0 : _b.duration) !== undefined;
        };
        (_b = ref === null || ref === void 0 ? void 0 : ref.catalog) === null || _b === void 0 ? void 0 : _b.getVideo(videoId, (error) => {
            if (!error) {
                setPlayer(ref);
                try {
                    waitUntil(videoIdIsSet).then(() => {
                        var _a, _b, _c, _d, _e, _f;
                        const uploadDate = (_a = ref === null || ref === void 0 ? void 0 : ref.mediainfo) === null || _a === void 0 ? void 0 : _a.publishedAt;
                        const dataVideoId = (_b = ref === null || ref === void 0 ? void 0 : ref.mediainfo) === null || _b === void 0 ? void 0 : _b.id;
                        const embedUrl = `https://players.brightcove.net/${(_c = ref === null || ref === void 0 ? void 0 : ref.bcinfo) === null || _c === void 0 ? void 0 : _c.accountId}/${ref === null || ref === void 0 ? void 0 : ref.bcinfo.playerId}_${(_d = ref === null || ref === void 0 ? void 0 : ref.bcinfo) === null || _d === void 0 ? void 0 : _d.embedId}/index.html?videoId=${dataVideoId}`;
                        const durationNum = (_e = ref === null || ref === void 0 ? void 0 : ref.mediainfo) === null || _e === void 0 ? void 0 : _e.duration;
                        const duration = `PT${Math.round(durationNum)}S`;
                        const thumbnailUrl = (posterImage === null || posterImage === void 0 ? void 0 : posterImage.url) || ((_f = ref === null || ref === void 0 ? void 0 : ref.mediainfo) === null || _f === void 0 ? void 0 : _f.poster);
                        setVideoSchema({
                            embedUrl,
                            uploadDate,
                            duration,
                            thumbnailUrl,
                            dataVideoId,
                            description,
                            name,
                        });
                        const videoElement = videoPlayerRefNode === null || videoPlayerRefNode === void 0 ? void 0 : videoPlayerRefNode.querySelector('video');
                        videoElement === null || videoElement === void 0 ? void 0 : videoElement.setAttribute('loop', true);
                        videoElement === null || videoElement === void 0 ? void 0 : videoElement.setAttribute('muted', true);
                        if (posterImage) {
                            videoElement === null || videoElement === void 0 ? void 0 : videoElement.removeAttribute('poster');
                        }
                        const vjsLoadingSpinner = videoPlayerRefNode === null || videoPlayerRefNode === void 0 ? void 0 : videoPlayerRefNode.querySelector('.vjs-loading-spinner .vjs-control-text');
                        if (vjsLoadingSpinner) {
                            vjsLoadingSpinner.innerHTML = labels === null || labels === void 0 ? void 0 : labels.videoLoadingText;
                        }
                        if (!isPlpVideo) {
                            trackVideo(videoElement, durationNum, articleCode);
                        }
                    });
                }
                catch (e) {
                    consoleEvents.error('Error with updating video player html', e);
                }
            }
            else {
                setVideoLoadingFailed(true);
                setIsStarted(false);
            }
        });
    };
    const onFailure = () => {
        consoleEvents.error('Error with loading product video from Brightcove');
        setVideoLoadingFailed(true);
    };
    const defaultIntersectionObserverOptions = {
        root: IS_CLIENT ? document : null,
        rootMargin: '0px 0px 150% 0px',
        threshold: 1,
    };
    return (React.createElement("figure", { className: styles.pdpVideo },
        React.createElement(IntersectionVisible, { active: true, onShow: handleOnShow, onHide: handleOnHide, options: Object.assign(Object.assign({}, defaultIntersectionObserverOptions), intersectionVisibleOptions) }, shouldUseAkamai ? (React.createElement("video", { autoPlay: true, loop: true, muted: true, playsInline: true, className: styles.akamaiVideo, ref: akamaiVideoRef },
            React.createElement("source", { src: getAkamaiVideoUrl(videoId || ''), type: "video/mp4" }))) : (React.createElement(ReactPlayerLoader, { accountId: options === null || options === void 0 ? void 0 : options.accountId, playerId: options === null || options === void 0 ? void 0 : options.playerId, videoId: videoId, onSuccess: onSuccess, onFailure: onFailure, ref: videoPlayerRef }))),
        !isStarted && posterImage && (React.createElement(Image, { url: (posterImage === null || posterImage === void 0 ? void 0 : posterImage.url) || '', altText: posterImage === null || posterImage === void 0 ? void 0 : posterImage.altText, imageType: imageTypes.PRODUCT_IMAGE, reserveHeightAspectRatio: reserveHeightAspectRatio, className: styles.posterImage })),
        !videoLoadingFailed && !isPlpVideo && (React.createElement(PlayVideoToggleButton, { onVideoClickEvent: handleOnClick, isPlaying: isPlaying, labels: labels, isTouchDevice: isTouchDevice, title: name }))));
};
export default ProductVideo;
//# sourceMappingURL=ProductVideo.js.map